// Prototype domain: one company-scoped employee identity, referenced by every run.
export const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
export const uid = () =>
  `pr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
export const money = (cents, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
export const parseAmount = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text))
    throw new Error(
      "Use a positive amount without commas, with up to two decimal places.",
    );
  const cents = Math.round(Number(text) * 100);
  if (!Number.isSafeInteger(cents) || cents <= 0 || cents > 1e14)
    throw new Error("Enter a valid positive payment amount.");
  return cents;
};
export function initialState() {
  const date = new Date();
  const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  return {
    version: 1,
    companyId: "prototype-cedar",
    period,
    paymentDate: `${period}-28`,
    bank: null,
    runs: [],
    overrides: {},
    excluded: [],
    employees: [
      {
        id: "emp-nour",
        employeeId: "E101",
        companyId: "prototype-cedar",
        name: "Nour Khoury",
        email: "nour@example.com",
        role: "Product designer",
        source: "My Team",
        currency: "USD",
        recurring: 150000,
        account: "DEMO-1001",
        active: true,
        enrolled: false,
        hireId: "hire-nour",
      },
      {
        id: "emp-karim",
        employeeId: "E102",
        companyId: "prototype-cedar",
        name: "Karim Haddad",
        email: "karim@example.com",
        role: "Account manager",
        source: "My Team",
        currency: "USD",
        recurring: 200000,
        account: "DEMO-1002",
        active: true,
        enrolled: false,
      },
      {
        id: "emp-maya",
        employeeId: "E103",
        companyId: "prototype-cedar",
        name: "Maya Saad",
        email: "maya@example.com",
        role: "Operations lead",
        source: "My Team",
        currency: "USD",
        recurring: 120000,
        account: "",
        active: true,
        enrolled: false,
      },
    ],
    hires: [
      {
        id: "hire-nour",
        employeeKey: "emp-nour",
        name: "Nour Khoury",
        email: "nour@example.com",
        role: "Product designer",
        hiredOn: "14 Sep",
      },
      {
        id: "hire-rami",
        name: "Rami Nasser",
        email: "rami@example.com",
        role: "Frontend engineer",
        hiredOn: "22 Sep",
      },
      {
        id: "hire-lea",
        name: "Lea Mansour",
        email: "lea@example.com",
        role: "Customer success",
        hiredOn: "26 Sep",
      },
    ],
  };
}
export function matchPerson(row, state) {
  const employees = state.employees.filter(
    (e) => e.companyId === state.companyId,
  );
  const byId = employees.filter(
    (e) =>
      row.employeeId && normalize(e.employeeId) === normalize(row.employeeId),
  );
  const byEmail = employees.filter(
    (e) => row.email && normalize(e.email) === normalize(row.email),
  );
  const byLink = employees.filter(
    (e) =>
      (row.employeeKey && e.id === row.employeeKey) ||
      (row.hireId && e.hireId === row.hireId) ||
      (row.applicantId && e.applicantId === row.applicantId),
  );
  const matches = [
    ...new Map([...byLink, ...byId, ...byEmail].map((e) => [e.id, e])).values(),
  ];
  if (matches.length > 1)
    return {
      kind: "review",
      reason: "Identifiers point to different employees.",
    };
  if (matches.length === 1) {
    const match = matches[0];
    if (
      (row.email &&
        match.email &&
        normalize(row.email) !== normalize(match.email)) ||
      (row.employeeId &&
        match.employeeId &&
        normalize(row.employeeId) !== normalize(match.employeeId))
    )
      return {
        kind: "review",
        reason: "An identifier conflicts with the existing record.",
      };
    return {
      kind: "employee",
      id: match.id,
      reason: byLink.length
        ? "Linked JOBSEEQR hire"
        : byId.length
          ? "Employee ID match"
          : "Exact email match",
    };
  }
  const hires = state.hires.filter(
    (h) =>
      (row.hireId && h.id === row.hireId) ||
      (row.email && normalize(h.email) === normalize(row.email)),
  );
  if (hires.length === 1)
    return { kind: "hire", id: hires[0].id, reason: "Matches a JOBSEEQR hire" };
  if (
    hires.length > 1 ||
    employees.some((e) => normalize(e.name) === normalize(row.name)) ||
    state.hires.some((h) => normalize(h.name) === normalize(row.name))
  )
    return { kind: "review", reason: "Similar name. Confirm who this is." };
  return { kind: "new", reason: "No existing employee or hire" };
}
export function previewRows(rows, state) {
  const seen = new Set();
  return rows.map((row, index) => {
    const match = matchPerson(row, state);
    const keys = [
      row.employeeId && `id:${normalize(row.employeeId)}`,
      row.email && `email:${normalize(row.email)}`,
      ["employee", "hire"].includes(match.kind) && `${match.kind}:${match.id}`,
    ].filter(Boolean);
    const duplicate = keys.some((k) => seen.has(k));
    keys.forEach((k) => seen.add(k));
    return {
      ...row,
      key: index,
      match,
      choice: duplicate
        ? "skip"
        : match.kind === "review"
          ? ""
          : `${match.kind}:${match.id || ""}`,
      duplicate,
    };
  });
}
export function applyRows(state, rows, recurring) {
  const next = JSON.parse(JSON.stringify(state));
  const touched = new Set();
  for (const row of rows) {
    if (row.choice === "skip") continue;
    if (!row.choice) throw new Error("Resolve every match before importing.");
    const [kind, key] = row.choice.split(":");
    let employee =
      kind === "employee"
        ? next.employees.find(
            (e) => e.id === key && e.companyId === next.companyId,
          )
        : null;
    let hire = kind === "hire" ? next.hires.find((h) => h.id === key) : null;
    if (kind === "employee" && !employee)
      throw new Error("The selected employee no longer exists.");
    if (kind === "hire" && !hire)
      throw new Error("The selected hire no longer exists.");
    if (hire) {
      const match = matchPerson({ ...hire, hireId: hire.id }, next);
      if (match.kind === "employee")
        employee = next.employees.find((e) => e.id === match.id);
      else if (match.kind === "review")
        throw new Error("This hire needs an explicit employee match.");
    }
    if (!employee) {
      if (!row.name?.trim() || (!row.email?.trim() && !row.employeeId?.trim()))
        throw new Error(
          "New employees need a name and an email or employee ID.",
        );
      const exact = next.employees.some(
        (e) =>
          (row.email && normalize(e.email) === normalize(row.email)) ||
          (row.employeeId &&
            normalize(e.employeeId) === normalize(row.employeeId)),
      );
      if (exact)
        throw new Error(
          "This identity already exists. Link to that employee instead of creating another.",
        );
      employee = {
        id: uid(),
        companyId: next.companyId,
        name: row.name.trim(),
        email: row.email || "",
        employeeId: row.employeeId || "",
        role: hire?.role || row.role || "",
        currency: row.currency || "USD",
        recurring: null,
        account: "",
        active: true,
        source: hire ? "JOBSEEQR hire" : "Import",
      };
      next.employees.push(employee);
    }
    if (touched.has(employee.id))
      throw new Error(
        "Two rows point to the same employee. Skip one before importing.",
      );
    touched.add(employee.id);
    if (hire || row.hireId) {
      const linkedHire = hire || next.hires.find((h) => h.id === row.hireId);
      if (linkedHire) {
        linkedHire.employeeKey = employee.id;
        employee.hireId = linkedHire.id;
      }
    }
    // Blank cells never erase saved information; identity fields are not overwritten by salary imports.
    if (row.account) employee.account = row.account;
    if (row.currency) {
      if (
        row.currency !== employee.currency &&
        row.amount == null &&
        employee.recurring != null
      )
        throw new Error("A currency change requires an explicit amount.");
      employee.currency = row.currency;
    }
    employee.enrolled = row.enrolled !== false;
    if (next.includedIds) {
      next.includedIds = next.includedIds.filter((id) => id !== employee.id);
      if (employee.enrolled) next.includedIds.push(employee.id);
    }
    next.excluded = (next.excluded || []).filter((id) => id !== employee.id);
    if (row.amount != null) {
      next.overrides[employee.id] = row.amount;
      if (recurring) employee.recurring = row.amount;
    }
  }
  return next;
}
export const amountFor = (state, employee) =>
  state.overrides[employee.id] ?? employee.recurring;
export const isIncluded = (state, employee) =>
  state.includedIds
    ? state.includedIds.includes(employee.id)
    : employee.enrolled;
export const paymentIssue = (state, employee) => {
  const duplicate = state.employees.some(
    (other) =>
      other.id !== employee.id &&
      isIncluded(state, other) &&
      other.active &&
      !state.excluded?.includes(other.id) &&
      ((employee.employeeId &&
        normalize(other.employeeId) === normalize(employee.employeeId)) ||
        (employee.email &&
          normalize(other.email) === normalize(employee.email)) ||
        (employee.hireId && other.hireId === employee.hireId) ||
        (employee.applicantId && other.applicantId === employee.applicantId)),
  );
  if (duplicate) return "Duplicate employee selected";
  return !employee.account?.trim()
    ? "Add bank details"
    : !amountFor(state, employee)
      ? "Add payment amount"
      : "";
};
export const totalsFor = (rows) =>
  rows.reduce(
    (totals, row) => ({
      ...totals,
      [row.currency]: (totals[row.currency] || 0) + (row.amount || 0),
    }),
    {},
  );
export function createRun(state) {
  if (!state.bank) throw new Error("Set up the demo paying account first.");
  const employees = state.employees.filter(
    (e) => isIncluded(state, e) && e.active && !state.excluded?.includes(e.id),
  );
  if (!employees.length || employees.some((e) => paymentIssue(state, e)))
    throw new Error("Complete the selected employees’ payment details first.");
  if (!state.paymentDate || state.paymentDate.slice(0, 7) !== state.period)
    throw new Error("Choose a payment date within this payroll month.");
  if (state.runs.some((r) => r.period === state.period))
    throw new Error(
      "This month has already been submitted. Open its payment results.",
    );
  return {
    id: uid(),
    period: state.period,
    date: state.paymentDate,
    approvedAt: new Date().toISOString(),
    bank: { ...state.bank },
    entries: employees.map((e) => ({
      employeeId: e.id,
      employeeNumber: e.employeeId,
      name: e.name,
      account: e.account,
      currency: e.currency,
      amount: amountFor(state, e),
      status: "submitted",
      attempts: 1,
    })),
  };
}
